// WallOfFameStrip — social-proof widget for the homepage hero.
// Fetches the anonymized `loyalty/public-fame` endpoint and shows overlapping
// avatar circles (initials) with a tally of returning guests. Silently
// renders nothing when the backend hasn't returned enough loyal guests yet.
import React, { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const RET = { en: 'guests have stayed with us two or more times (our booking records)', me: 'gostiju odsjelo je kod nas dva ili više puta (naša evidencija)', sq: 'mysafirë kanë qëndruar te ne dy ose më shumë herë (regjistrat tanë)', de: 'Gäste waren zwei- oder mehrmals bei uns (unsere Buchungsdaten)', ru: 'гостей останавливались у нас два и более раз (наши данные бронирований)', pl: 'gości zatrzymało się u nas dwa lub więcej razy (nasze rezerwacje)', hu: 'vendég szállt meg nálunk kétszer vagy többször (foglalási adataink)' };

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Deterministic HSL gradient from a string so the same guest always gets the
// same avatar color across renders and reloads.
const hueFromString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
  return h % 360;
};

const flagOf = (cc) => {
  if (!cc || cc.length !== 2) return '';
  const A = 0x1F1E6;
  try {
    const points = cc.toUpperCase().split('').map((c) => A + (c.charCodeAt(0) - 65));
    return String.fromCodePoint(...points);
  } catch {
    return '';
  }
};

const WallOfFameStrip = () => {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/loyalty/public-fame?limit=6`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch { /* silently ignore — non-critical widget */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.items?.length || data.count_2plus < 3) return null;

  const avatars = data.items.map((g, i) => {
    const hue = hueFromString(g.first_name + g.initials);
    return (
      <div
        key={`${g.first_name}-${i}`}
        className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-white shadow-md"
        style={{
          background: `linear-gradient(135deg, hsl(${hue},70%,55%), hsl(${(hue + 40) % 360},65%,45%))`,
          marginLeft: i === 0 ? 0 : -12,
        }}
        title={`${g.first_name} · ${g.visits} stays${g.country ? ' · ' + g.country : ''}`}
      >
        {g.initials}
      </div>
    );
  });

  return (
    <div
      className="flex items-center gap-3 bg-white/10 backdrop-blur-sm w-fit px-4 py-2 rounded-full border border-white/20"
      data-testid="wall-of-fame-strip"
    >
      <div className="flex items-center">{avatars}</div>
      <div className="flex items-center gap-2 text-white">
        <Heart size={16} className="fill-pink-500 text-pink-500" />
        <span className="text-sm font-medium">
          <span className="font-bold">{data.count_2plus}</span> {RET[lang] || RET.en}
        </span>
        <span className="hidden md:inline text-lg">
          {data.items.slice(0, 4).map((g) => flagOf(g.country)).filter(Boolean).join(' ')}
        </span>
      </div>
    </div>
  );
};

export default WallOfFameStrip;
